<template>
  <div class="card shadow-sm p-3">
    <div class="d-flex align-items-center gap-2 mb-3">
      <span class="material-icons text-info">assignment</span>
      <span class="fw-bold">Formulario 012A — Planilla {{ numero }}</span>
    </div>

    <button
      class="btn btn-info btn-sm text-white d-flex align-items-center gap-1"
      @click="validar"
      :disabled="cargando"
    >
      <span v-if="cargando" class="spinner-border spinner-border-sm"></span>
      <span v-else class="material-icons" style="font-size:16px">verified</span>
      {{ cargando ? 'Validando...' : 'Validar 012A' }}
    </button>

    <!-- Error archivo -->
    <div v-if="errorMensaje" class="alert alert-danger small mt-3">
      <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">error</span>
      {{ errorMensaje }}
    </div>

    <div v-if="resultado" class="mt-3">
      <hr />

      <!-- Alerta resumen -->
      <div class="alert py-2 small" :class="alertaClase">
        <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">
          {{ todoOk ? 'check_circle' : 'warning' }}
        </span>
        {{ todoOk
            ? 'El formulario 012A es válido.'
            : 'Se encontraron diferencias en el formulario 012A.' }}
      </div>

      <!-- ── Tabla comparación ───────────────────────────────────────────────── -->
      <table class="table table-sm table-bordered small">
        <thead class="table-dark">
          <tr>
            <th>Campo</th>
            <th>Base de datos</th>
            <th>Formulario 012A</th>
            <th style="width:50px" class="text-center">OK</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="fila in filasComparacion" :key="fila.campo"
              :class="fila.coincide === false ? 'table-danger' : ''">
            <td class="fw-bold">{{ fila.campo }}</td>
            <td>{{ fila.bd ?? '-' }}</td>
            <td>{{ fila.pdf ?? '-' }}</td>
            <td class="text-center">
              <span v-if="fila.coincide === true"  class="badge bg-success">✓</span>
              <span v-else-if="fila.coincide === false" class="badge bg-danger">✗</span>
              <span v-else class="text-muted small">—</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- ── Diagnósticos CIE-10 ────────────────────────────────────────────── -->
      <p class="fw-bold small mb-2 mt-3">Diagnósticos CIE-10</p>

      <div class="mb-1 small">
        <span class="fw-bold text-success me-1">✓ Coinciden:</span>
        <template v-if="resultado.diagnosticosCoinciden?.length">
          <span v-for="d in resultado.diagnosticosCoinciden" :key="d"
                class="badge bg-success me-1">{{ d }}</span>
        </template>
        <span v-else class="text-muted">Ninguno</span>
      </div>

      <div class="mb-1 small">
        <span class="fw-bold text-warning me-1">⚠ En BD, no en 012A:</span>
        <template v-if="resultado.diagnosticosNoPdf?.length">
          <span v-for="d in resultado.diagnosticosNoPdf" :key="d"
                class="badge bg-warning text-dark me-1">{{ d }}</span>
        </template>
        <span v-else class="text-muted">Ninguno</span>
      </div>

      <div class="mb-1 small">
        <span class="fw-bold text-info me-1">⚠ En 012A, no en BD:</span>
        <template v-if="resultado.diagnosticosNoBd?.length">
          <span v-for="d in resultado.diagnosticosNoBd" :key="d"
                class="badge bg-info text-dark me-1">{{ d }}</span>
        </template>
        <span v-else class="text-muted">Ninguno</span>
      </div>

      <!-- ── Detalle diagnósticos del formulario ────────────────────────────── -->
      <p class="fw-bold small mb-1 mt-3">Diagnósticos en el formulario</p>
      <table class="table table-sm table-bordered small">
        <thead class="table-secondary">
          <tr>
            <th>Descripción</th>
            <th style="width:65px" class="text-center">CIE</th>
            <th style="width:42px" class="text-center">Pre</th>
            <th style="width:42px" class="text-center">Def</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in resultado.diagnosticosPdf" :key="d.codigoCie"
              :class="cieBdSet.has(d.codigoCie) ? 'table-success' : ''">
            <td>{{ d.descripcion }}</td>
            <td class="text-center">
              <span class="badge bg-secondary">{{ d.codigoCie }}</span>
            </td>
            <td class="text-center">
              <span v-if="d.presuntivo" class="badge bg-warning text-dark">P</span>
            </td>
            <td class="text-center">
              <span v-if="d.definitivo" class="badge bg-success">D</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- ── Profesional ────────────────────────────────────────────────────── -->
      <p class="fw-bold small mb-1 mt-3">Profesional responsable</p>
      <div class="row g-2 small">
        <div class="col-6">
          <label class="fw-bold">Nombre</label>
          <input type="text" class="form-control form-control-sm"
                 :value="resultado.nombreProfesional" readonly />
        </div>
        <div class="col-3">
          <label class="fw-bold">Cédula</label>
          <input type="text" class="form-control form-control-sm"
                 :value="resultado.cedulaProfesional" readonly />
        </div>
        <div class="col-3">
          <label class="fw-bold">Fecha / Hora</label>
          <input type="text" class="form-control form-control-sm"
                 :value="resultado.fechaPdf && resultado.horaPdf
                          ? `${resultado.fechaPdf} ${resultado.horaPdf}`
                          : resultado.fechaPdf ?? '-'" readonly />
        </div>
      </div>

      <!-- ── Texto colapsable ───────────────────────────────────────────────── -->
      <div class="mt-3">
        <button class="btn btn-outline-secondary btn-sm"
                @click="mostrarTexto = !mostrarTexto">
          <span class="material-icons me-1" style="font-size:14px;vertical-align:middle">
            {{ mostrarTexto ? 'expand_less' : 'expand_more' }}
          </span>
          {{ mostrarTexto ? 'Ocultar texto' : 'Ver texto completo del PDF' }}
        </button>
        <textarea v-if="mostrarTexto"
                  class="form-control form-control-sm mt-2"
                  rows="6" :value="resultado.textoCompleto" readonly />
      </div>
    </div>
  </div>
</template>

<script>
import api  from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "Formulario012AValidador",

  props: {
    idTramite:   { type: Number,  required: true },
    numero:      { type: String,  required: true },
    autoValidar: { type: Boolean, default: false },
  },

  emits: ['resultado'],

  data() {
    return {
      resultado:    null,
      cargando:     false,
      errorMensaje: null,
      mostrarTexto: false,
    };
  },

  computed: {
    todoOk() {
      return this.resultado?.cedulaCoincide
          && this.resultado?.nombreCoincide
          && this.resultado?.fechaEnRango;
    },

    alertaClase() {
      return this.todoOk ? 'alert-success' : 'alert-warning';
    },

    /** Set de CIEs de BD para resaltar filas en la tabla de diagnósticos */
    cieBdSet() {
      return new Set([
        this.resultado?.diag1Bd,
        this.resultado?.diag2Bd,
        this.resultado?.diag3Bd,
        this.resultado?.diag4Bd,
        this.resultado?.diag5Bd,
      ].filter(Boolean).map(d => d.toUpperCase()));
    },

    filasComparacion() {
      if (!this.resultado) return [];
      return [
        // ── Campos con comparación BD vs PDF ──────────────────────────────────
        {
          campo:    'Cédula',
          bd:       this.resultado.cedulaBd,
          pdf:      this.resultado.cedulaPdf,
          coincide: this.resultado.cedulaCoincide,
        },
        {
          campo:    'Nombre',
          bd:       this.resultado.nombreBd,
          // ► Aquí estaba el bug: usaba un campo inexistente.
          //   nombrePdf existe en el DTO y llega en el JSON.
          pdf:      this.resultado.nombrePdf,
          coincide: this.resultado.nombreCoincide,
        },
        {
          campo:    'Fecha atención (rango BD)',
          bd:       `${this.resultado.fechaDesdeBd ?? '-'}  →  ${this.resultado.fechaHastaBd ?? '-'}`,
          pdf:      this.resultado.fechaPdf,
          coincide: this.resultado.fechaEnRango,
        },
        // ── Solo informativos (sin comparación) ───────────────────────────────
        {
          campo:    'Institución',
          bd:       '-',
          pdf:      this.resultado.institucionPdf,
          coincide: null,
        },
        {
          campo:    'Establecimiento',
          bd:       '-',
          pdf:      this.resultado.establecimientoPdf,
          coincide: null,
        },
        {
          campo:    'No. HCU',
          bd:       '-',
          pdf:      this.resultado.numeroHcuPdf,
          coincide: null,
        },
        {
          campo:    'Motivo solicitud',
          bd:       '-',
          pdf:      this.resultado.motivoSolicitudPdf,
          coincide: null,
        },
      ];
    },
  },

  mounted() {
    if (this.autoValidar) this.validar();
  },

  methods: {
    async ejecutarValidacion() {
      await this.validar();
    },

    async validar() {
      this.cargando     = true;
      this.resultado    = null;
      this.errorMensaje = null;
      this.mostrarTexto = false;

      try {
        const resp = await api.get(
          `/validacion-012a/${this.idTramite}/${this.numero}`);
        const v = resp.data;

        if (!v.archivoEncontrado) {
          this.errorMensaje = v.mensajeError ?? "Archivo 012A.pdf no encontrado.";
          this.$emit('resultado', 'sin-archivo');
          return;
        }

        this.resultado = v;
        this.$emit('resultado', this.calcularEstado());
      } catch (e) {
        console.error(e);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo validar el formulario 012A.",
        });
      } finally {
        this.cargando = false;
      }
    },

    calcularEstado() {
      if (!this.resultado) return null;
      if (!this.resultado.archivoEncontrado) return 'sin-archivo';

      const hayError =
        !this.resultado.cedulaCoincide  ||
        !this.resultado.nombreCoincide  ||
        !this.resultado.fechaEnRango;

      const hayLeve =
        this.resultado.diagnosticosNoPdf?.length > 0 ||
        this.resultado.diagnosticosNoBd?.length  > 0;

      if (hayError) return 'error';
      if (hayLeve)  return 'leve';
      return 'ok';
    },
  },
};
</script>